"use client";
import React from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

interface NoResultsFoundProps {
    title?: string;
    description?: string;
    onReset?: () => void;
    resetLabel?: string;
    searchTerm?: string;
    activeFiltersCount?: number;
}

const NoResultsFound: React.FC<NoResultsFoundProps> = ({
    title = "No classes found",
    description,
    onReset,
    resetLabel = "Clear All Filters",
    searchTerm,
    activeFiltersCount = 0,
}) => {
    const defaultDescription =
        searchTerm && searchTerm.trim()
            ? `We couldn't find any classes matching "${searchTerm}".`
            : activeFiltersCount > 0
                ? `No classes match your current filters. Try adjusting or clearing them.`
                : `We couldn't find any classes matching your criteria.`;

    return (
        <div className="flex flex-col items-center justify-center py-16 px-6">
            {/* Illustration */}
            <div className="relative mb-8">
                {/* Background circle */}
                <div className="w-32 h-32 bg-gradient-to-br from-orange-50 to-orange-100 rounded-full flex items-center justify-center">
                    {/* Inner icon */}
                    <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center">
                        <MagnifyingGlassIcon className="w-10 h-10 text-orange-300" />
                    </div>
                </div>
                {/* Decorative dots */}
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-orange-200 rounded-full opacity-60" />
                <div className="absolute -bottom-1 -left-3 w-4 h-4 bg-orange-300 rounded-full opacity-40" />
                <div className="absolute top-4 -left-4 w-3 h-3 bg-orange-100 rounded-full opacity-80" />
            </div>

            {/* Text */}
            <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">
                {title}
            </h3>
            <p className="text-gray-500 text-center max-w-sm mb-6 text-sm leading-relaxed">
                {description || defaultDescription}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
                {onReset && activeFiltersCount > 0 && (
                    <button
                        onClick={onReset}
                        className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-300 text-sm font-medium shadow-md hover:shadow-lg"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                        </svg>
                        {resetLabel}
                    </button>
                )}
                {searchTerm && searchTerm.trim() && onReset && (
                    <button
                        onClick={onReset}
                        className="inline-flex items-center justify-center gap-2 px-6 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 text-sm font-medium"
                    >
                        Clear Search
                    </button>
                )}
            </div>

            {/* Suggestions */}
            <div className="mt-8 text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-3">
                    Suggestions
                </p>
                <ul className="text-sm text-gray-500 space-y-1.5">
                    <li>• Try different search keywords</li>
                    <li>• Broaden your filter criteria</li>
                    <li>• Check back later for new classes</li>
                </ul>
            </div>
        </div>
    );
};

export default NoResultsFound;
